import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import googleReviewsService from '../../../services/googleReviewsService';

// Component to handle expandable review text
const ExpandableReviewText = ({ text }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    // Check if text is truncated (exceeds 4 lines)
    if (textRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight);
      const maxHeight = lineHeight * 4; // 4 lines
      setIsTruncated(textRef.current.scrollHeight > maxHeight);
    }
  }, []); // Empty dependency - only check on mount

  return (
    <div>
      <p
        ref={textRef}
        className={`text-sm text-muted-foreground leading-relaxed transition-all duration-300 ${!isExpanded ? 'line-clamp-4' : ''
          }`}
      >
        {text}
      </p>

      {isTruncated && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors inline-flex items-center gap-1"
        >
          {isExpanded ? (
            <>
              Show less
              <Icon name="ChevronUp" size={14} />
            </>
          ) : (
            <>
              Read more
              <Icon name="ChevronDown" size={14} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

const GoogleReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, averageRating: 0 });
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const data = await googleReviewsService.getAllReviews();
      setReviews(data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await googleReviewsService.getReviewStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filteredReviews = selectedCategory === 'all'
    ? reviews
    : reviews.filter(r => r.treatment_category?.slug === selectedCategory);

  const categories = [
    { slug: 'all', name: 'All Reviews' },
    { slug: 'spine-joint-disorders', name: 'Bone & Joint Disorders' },
    { slug: 'general-wellness', name: 'General Wellness' },
    { slug: 'metabolic-endocrine-disorders', name: 'Metabolic Disorders' },
    { slug: 'autoimmune-disorders', name: 'Auto-Immune Disorders' },
    { slug: 'skin-care', name: 'Skin Disorders' },
    { slug: 'womens-health', name: "Women's Health" },
    { slug: 'neurological-stress-related-disorders', name: 'Neurological & Stress Related Disorders' },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[...Array(5)].map((_, i) => (
          <Icon
            key={i}
            name="Star"
            size={16}
            className={i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt="Google"
              className="h-8"
            />
            <span className="text-2xl font-semibold text-foreground">Reviews</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <Icon key={i} name="Star" size={24} className="text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <span className="text-3xl font-bold text-foreground">{stats.averageRating}</span>
          </div>
          <p className="text-muted-foreground">
            Based on {stats.total} authentic Google reviews from our patients
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map((category) => (
            <button
              key={category.slug}
              onClick={() => setSelectedCategory(category.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category.slug
                ? 'bg-primary text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReviews.slice(0, 12).map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="glass-card p-6 rounded-xl hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{review.author_name}</h4>
                    <p className="text-xs text-muted-foreground">{formatDate(review.review_time)}</p>
                  </div>
                </div>
                <img
                  src="https://www.google.com/images/branding/product/1x/gsa_512dp.png"
                  alt="Google"
                  className="w-6 h-6"
                />
              </div>

              {/* Rating */}
              <div className="mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Category Badge */}
              {review.treatment_category && (
                <div className="mb-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                    <Icon name="Stethoscope" size={12} />
                    {review.treatment_category.name}
                  </span>
                </div>
              )}

              {/* Review Text */}
              {review.review_text && (
                <ExpandableReviewText text={review.review_text} />
              )}

              {/* Verified Badge */}
              <div className="mt-4 pt-4 border-t border-border flex items-center gap-2">
                <Icon name="CheckCircle2" size={14} className="text-green-500" />
                <span className="text-xs text-muted-foreground">Verified Google Review</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        {filteredReviews.length > 12 && (
          <div className="text-center mt-8">
            <a
              href="https://www.google.com/maps/search/?api=1&query=Sri+Dhanwantari+AyurVaidik+Parishkar+Kendra&query_place_id=ChIJneaqsYeTyzsRxgWwghv46M8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
            >
              View All Reviews on Google
              <Icon name="ExternalLink" size={16} />
            </a>
          </div>
        )}

        {/* Empty State */}
        {filteredReviews.length === 0 && (
          <div className="text-center py-12">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No reviews found for this category.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default GoogleReviewsSection;

